import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Container, Modal, Row, Col } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import Select from "react-select";
import { usePartCategory } from "../../../shared/custom-hooks/usePartCategory";

import {
  FormContainer,
  FormGroupLabel,
  FormItem,
} from "../../../shared/components/form/FormElements";
import { Card, CardBody } from "../../../shared/components/Card";
import { formInputStyles, formStyles } from "../../../shared/constants/styles";
import FormField from "../../../shared/components/form/FormField";
import Error from "@/shared/components/form/Error";
import {
  ActionIconContainer,
  AddFormButton,
  CustomLoader,
} from "../../../shared/components/TableElements";
import { PhotoUploadComponent } from "../../UserManagement/UserDetailsForm";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { postApi } from "../../../api/getUser";
import {
  handleNegative,
  handleNumberInputOnWheelPreventChange,
} from "../../../shared/helpers";
import ErrorPopup from "../../../shared/components/ErrorPopup";
import { uploadImgToS3 } from "../../Issues/IssuesForm";
import { useAuth } from "../../../contexts/AuthContext";
import { s3Url } from "../../../shared/constants";
import AddPartCategoryPopup from "../components/AddPartCategoryPopup";
import CreatableSelect from "react-select/creatable";
import CustomMenuList from "../../../shared/components/form/CustomMenuList";
import PartCategoryOption from "../components/PartCategoryOption";

const EditPartPopup = ({
  showPopup,
  setShowPopup,
  rowData,
  setSnackbarMsg,
}) => {
  const { t } = useTranslation("common");
  const { setReload } = useAuth();
  const { currency } = useSelector((state) => state.units);
  const { orgId, userId } = useSelector((state) => state.user);

  const allAssets = useSelector((state) => state.vehicles.list);
  const activeVehicles = useMemo(
    () => allAssets?.filter((v) => v.veh_active === 1 && v.asset_type === 0),
    [allAssets]
  );
  const allPartCategories = useSelector(
    (state) => state.parts.allPartCategories
  );
  const {
    handleSubmit,
    control,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm();

  const partName = watch("partName");
  const partNo = watch("partNo");
  const partNotes = watch("partNotes");
  const barcode = watch("barcode");
  const selectedVehicles = watch("selectedVehicles");
  const partCategory = watch("partCategory");
  const reorderAt = watch("reorderAt");
  const [partPhoto, setPartPhoto] = useState("");
  const [saveBtnLoading, setSaveBtnLoading] = useState(false);

  const [showErrPopup, setShowErrPopup] = useState(false);
  const [syncErrMsg, setSyncErrMsg] = useState("");

  // Use custom hook for part category functionality
  const {
    showPartCategoryPopup,
    setShowPartCategoryPopup,
    tempPartCategoryData,
    setTempPartCategoryData,
    partCategoryPopupErr,
    isPartCategorySaving,
    isPartCategoryDeleting,
    handleCreatePartCategory,
    handleSavePartCategory,
    handleDeletePartCategory,
    handleClosePartCategoryPopup,
  } = usePartCategory();

  //Effect on change of rowData(edit)
  useEffect(() => {
    if (rowData && orgId && activeVehicles?.length) {
      const {
        part_name,
        part_number,
        part_notes,
        part_img,
        part_id,
        barcode,
        veh_ids,
        category_id,
        category_name,
        reorder_at,
      } = rowData;
      setValue("partName", part_name);
      setValue("partNo", part_number);
      setValue("partNotes", part_notes);
      setValue("barcode", barcode);
      setValue("reorderAt", +reorder_at > 0 ? +reorder_at : "");

      // Set part category if it exists
      if (category_id && category_name) {
        setValue("partCategory", { label: category_name, value: category_id });
      }

      (async () => {
        const partVehRes = await postApi(
          {
            querystring: "fetch_single_part_veh",
            part_id,
          },
          "inventory"
        );
        let selected = [];
        if (partVehRes.success) {
          const { inventory_data } = partVehRes;

          const activeVehicleIds = activeVehicles.map((v) => v.veh_id);
          for (let obj of inventory_data) {
            const { part_vehicle_id, veh_id } = obj;
            if (activeVehicleIds.includes(veh_id)) {
              const vehicle = activeVehicles.find((v) => v.veh_id === veh_id);
              selected.push({ ...vehicle, part_vehicle_id });
            } else {
              selected.push({
                value: veh_id,
                veh_id: veh_id,
                label: t("unassigned_asset"),
                name: t("unassigned_asset"),
                veh_img: "",
                part_vehicle_id,
                isRemoveDisabled: true,
              });
            }
          }

          // const vehIdsArr = veh_ids.split(":::");
          // console.log("vehIdsArr", vehIdsArr);
          // for (let id of vehIdsArr) {
          //   if (activeVehicleIds.includes(id)) {
          //     const vehicle = activeVehicles.find((v) => v.veh_id === id);
          //     console.log("vehicle", vehicle);
          //     selected.push(vehicle);
          //   } else {
          //     selected.push({
          //       value: id,
          //       veh_id: id,
          //       label: t("unassigned_asset"),
          //       name: t("unassigned_asset"),
          //       veh_img: "",
          //     });
          //   }
          // }
        }
        setValue("selectedVehicles", selected);
      })();

      //Logic to set Part Image
      if (part_img) {
        const imageUrl = `${s3Url}/org_${orgId}/parts/${part_id}/${part_img}`;

        setPartPhoto({ url: imageUrl, imgName: part_img });
      }
      //else {
      //   setPartPhoto("");
      // }
    }
  }, [rowData, orgId, activeVehicles]);

  const handleErrPopupClose = () => {
    setShowErrPopup(false);
    setSyncErrMsg("");
  };

  const onSubmit = async () => {
    try {
      setSaveBtnLoading(true);
      let assignedVeh = [];
      if (selectedVehicles?.length) {
        let count = 1;
        for (let veh of selectedVehicles) {
          assignedVeh.push({
            veh_id: veh.veh_id,
            part_vehicle_id: veh.hasOwnProperty("part_vehicle_id")
              ? veh.part_vehicle_id
              : `${userId}_${Date.now() + count}`,
          });
          count += 1;
        }
      }
      const partApiJson = {
        querystring: "add_edit_parts",
        part_id: rowData?.part_id,
        part_name: partName,
        part_number: partNo || "",
        notes: partNotes || "",
        barcode: barcode || null,
        image: partPhoto?.imgName || "",
        veh_ids: assignedVeh,
        action: 2,
        category_id: partCategory?.value || "",
        reorder_at: +reorderAt > 0 ? +reorderAt : 0,
      };
      const partApiRes = await postApi(partApiJson, "inventory");

      if (!partApiRes.success) {
        if (partApiRes?.action_value === "record_exists") {
          setSyncErrMsg(t("dup_part_name_err"));
        }
        if (partApiRes?.action_value === "barcode_record_exists") {
          setSyncErrMsg(t("dup_barcode_err"));
        }
        if (partApiRes?.action_value === "part_number_record_exists") {
          setSyncErrMsg(t("dup_part_no_err"));
        }
        setSaveBtnLoading(false);
        setShowErrPopup(true);
        return;
      }

      if (partPhoto?.file) {
        const key = `image_data/org_${orgId}/parts/${rowData?.part_id}/${partPhoto?.imgName}`;
        const img = { file: partPhoto?.file, key };
        const status = await uploadImgToS3(img);
        if (status === 200) {
          setSaveBtnLoading(false);
          setShowPopup(false);
          setReload("parts");
          setSnackbarMsg(t("record_edited_msg", { recordName: t("part") }));
          return;
        }
      }

      setSaveBtnLoading(false);
      setShowPopup(false);
      setReload("parts");
      setSnackbarMsg(t("record_edited_msg", { recordName: t("part") }));
    } catch (e) {
      console.log(e);
      setSaveBtnLoading(false);
      setShowErrPopup(true);
      return;
    }
  };

  const handleClose = () => {
    setShowPopup(false);
  };

  return (
    <>
      {showErrPopup && (
        <ErrorPopup
          showPopup={showErrPopup}
          setShowPopup={setShowErrPopup}
          message={syncErrMsg || ""}
          onClose={handleErrPopupClose}
        />
      )}
      {showPartCategoryPopup && (
        <AddPartCategoryPopup
          showPopup={showPartCategoryPopup}
          setShowPopup={setShowPartCategoryPopup}
          categoryData={tempPartCategoryData}
          onSave={(data) =>
            handleSavePartCategory({ ...data, setValue, watch })
          }
          onDelete={(categoryId) =>
            handleDeletePartCategory(categoryId, setValue, watch)
          }
          onClose={handleClosePartCategoryPopup}
          isCreateNew={tempPartCategoryData?.__isNew__}
          error={partCategoryPopupErr}
          isSaving={isPartCategorySaving}
          isDeleting={isPartCategoryDeleting}
        />
      )}
      <Modal show={showPopup} size="lg" centered>
        <FormContainer
          className="m-0 p-0 d-block"
          onSubmit={handleSubmit(onSubmit)}
        >
          <Modal.Header>
            <h4>{t("edit_part")}</h4>
            <div
              className="d-flex justify-content-end"
              //style={{ width: "-webkit-fill-available" }}
            >
              <ActionIconContainer onClick={handleClose}>
                <i className="fi fi-rr-cross-small" />
              </ActionIconContainer>
            </div>
          </Modal.Header>
          <Modal.Body>
            <Container className="m-0 p-0">
              <Card className="m-0 p-0">
                <CardBody
                  //className="m-0 p-0"
                  style={{
                    height: `${window.innerHeight - 220}px`,
                    overflow: "auto",
                  }}
                >
                  <Row className="d-flex align-items-center">
                    <Col md={3}>
                      <PhotoUploadComponent
                        photo={partPhoto}
                        setPhoto={setPartPhoto}
                        module={"parts"}
                        //isDisabled={type === "edit"}
                        //rowData={rowData}
                      />
                    </Col>
                    <Col></Col>
                    <Col md={8}>
                      <Row>
                        <Col className="m-0 p-0">
                          <FormGroupLabel className="m-0 p-0">
                            {t("part_category")}
                          </FormGroupLabel>
                          <Controller
                            name="partCategory"
                            control={control}
                            errors={errors}
                            render={({ field: { onChange, value, ref } }) => (
                              <CreatableSelect
                                options={allPartCategories}
                                value={value}
                                onChange={onChange}
                                onCreateOption={handleCreatePartCategory}
                                placeholder={t("part_category_placeholder")}
                                styles={formStyles}
                                isDisabled={false}
                                components={{
                                  Option: (optionProps) => (
                                    <PartCategoryOption
                                      {...optionProps}
                                      setShowPartCategoryPopup={
                                        setShowPartCategoryPopup
                                      }
                                      setTempPartCategoryData={
                                        setTempPartCategoryData
                                      }
                                    />
                                  ),
                                  MenuList: CustomMenuList,
                                }}
                                customProps={{
                                  type: "partCategory",
                                }}
                                id="part-category-dropdown"
                                onCreateNew={() => handleCreatePartCategory("")}
                                createNewLabel={t("create_new_part_category")}
                                isClearable
                              />
                            )}
                            //rules={{ required: t("required_err") }}
                          />

                          {errors?.partCategory && (
                            <Error error={errors.partCategory.message} />
                          )}
                        </Col>
                      </Row>
                      <Row>
                        <Col className="m-0 p-0">
                          <FormGroupLabel>{t("part_name")}</FormGroupLabel>
                          <FormItem disabled={false}>
                            <FormField
                              name="partName"
                              control={control}
                              component="input"
                              defaultValue=""
                              placeholder={t("part_name")}
                              style={formInputStyles}
                              disabled={false}
                              rules={{ required: t("required_err") }}
                            />
                          </FormItem>

                          {errors?.partName && (
                            <Error error={errors.partName.message} />
                          )}
                        </Col>
                      </Row>
                      <Row>
                        <Col className="m-0 p-0">
                          <FormGroupLabel>{t("part_no")}</FormGroupLabel>
                          <FormItem disabled={false}>
                            <FormField
                              name="partNo"
                              control={control}
                              component="input"
                              defaultValue=""
                              placeholder={t("part_no")}
                              style={formInputStyles}
                              disabled={false}
                            />
                          </FormItem>

                          {errors?.partNo && (
                            <Error error={errors.partNo.message} />
                          )}
                        </Col>
                      </Row>
                      <Row>
                        <Col className="m-0 p-0">
                          <FormGroupLabel>{t("upc_barcode")}</FormGroupLabel>
                          <FormItem disabled={false}>
                            <FormField
                              name="barcode"
                              control={control}
                              component="input"
                              defaultValue=""
                              placeholder={t("upc_barcode")}
                              style={formInputStyles}
                              disabled={false}
                            />
                          </FormItem>

                          {errors?.barcode && (
                            <Error error={errors.barcode.message} />
                          )}
                        </Col>
                      </Row>

                      <Row>
                        <Col className="m-0 p-0">
                          <FormGroupLabel>
                            {t("assigned_assets")}
                          </FormGroupLabel>
                          <Controller
                            name="selectedVehicles"
                            control={control}
                            errors={errors}
                            render={({ field: { onChange, value, ref } }) => (
                              <Select
                                defaultValue={[]}
                                options={activeVehicles}
                                value={value}
                                onChange={onChange}
                                placeholder={t("assigned_to_all_assets")}
                                styles={formStyles}
                                isMulti
                                closeMenuOnSelect={false}
                                isClearable={false}
                                backspaceRemovesValue={false}
                                //onMenuOpen={scrollToBottom}
                                //hideSelectedOptions={false}
                                // components={{
                                //   Option,
                                // }}
                                isDisabled={false}
                              />
                            )}
                            // rules={{ required: true }}
                          />
                        </Col>
                      </Row>

                      <Row>
                        <Col className="m-0 p-0">
                          <FormGroupLabel>{t("reorder_point")}</FormGroupLabel>
                          <FormItem disabled={false}>
                            <FormField
                              name="reorderAt"
                              control={control}
                              component="input"
                              type="number"
                              onKeyDown={handleNegative}
                              onWheel={handleNumberInputOnWheelPreventChange}
                              defaultValue=""
                              placeholder={t("reorder_point")}
                              style={formInputStyles}
                              disabled={false}
                            />
                          </FormItem>
                          {errors?.reorderAt && (
                            <Error error={errors.reorderAt.message} />
                          )}
                          <p className="text-muted small mt-1 mb-0">
                            {t("reorder_point_help")}{" "}
                            <Link to="/setup/notifications">
                              {t("notification_settings")}
                            </Link>
                            .
                          </p>
                        </Col>
                      </Row>

                      <Row>
                        <Col className="m-0 p-0">
                          <FormGroupLabel>{t("notes")}</FormGroupLabel>
                          <FormItem disabled={false}>
                            <FormField
                              name="partNotes"
                              control={control}
                              component="input"
                              defaultValue=""
                              placeholder={t("notes")}
                              style={formInputStyles}
                              disabled={false}
                            />
                          </FormItem>

                          {errors?.notes && (
                            <Error error={errors.notes.message} />
                          )}
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Container>
          </Modal.Body>
          <Modal.Footer>
            <AddFormButton
              id="save-form-button"
              type="submit"
              //onClick={addServiceTask}
            >
              {saveBtnLoading ? <CustomLoader /> : t("save")}
            </AddFormButton>
          </Modal.Footer>
        </FormContainer>
      </Modal>
    </>
  );
};

export default EditPartPopup;
